import { useState } from 'react';
import { motion } from 'framer-motion';
import { projects } from '@/data/index';
import { ProjectCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';
import { IMAGES } from '@/assets/images';

const categories = [
  { id: 'all', label: 'Tous les Projets' },
  { id: 'residential', label: 'Résidentiel' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'furniture', label: 'Ameublement' },
];

export default function Portfolio() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={IMAGES.FURNITURE_SHOWROOM_2}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Notre Portfolio
            </h1>
            <p className="text-lg text-muted-foreground">
              Découvrez nos réalisations prestigieuses à Abidjan. Chaque projet reflète notre engagement envers l'excellence, l'innovation et le design d'exception.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="flex flex-wrap justify-center gap-3 mb-16"
          >
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                variant={activeCategory === category.id ? 'default' : 'outline'}
                className="rounded-full px-6 py-2 transition-all duration-200 hover:scale-105 active:scale-95"
                style={{
                  boxShadow: activeCategory === category.id
                    ? '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent), inset 0 1px 0 rgba(255,255,255, 0.1), inset 0 -1px 0 rgba(0,0,0, 0.1)'
                    : 'none',
                }}
              >
                {category.label}
              </Button>
            ))}
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredProjects.map((project) => (
              <motion.div key={project.id} variants={staggerItem}>
                <ProjectCard project={project} />
              </motion.div>
            ))}
          </motion.div>

          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={springPresets.gentle}
              className="text-center py-16"
            >
              <p className="text-muted-foreground text-lg">
                Aucun projet trouvé dans cette catégorie.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={IMAGES.INTERIOR_DESIGN_3}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à Transformer Votre Espace ?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contactez-nous dès aujourd'hui pour discuter de votre projet. Notre équipe d'experts est prête à donner vie à vos idées avec créativité et professionnalisme.
            </p>
            <Button
              size="lg"
              className="rounded-full px-8 py-6 text-lg transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent), inset 0 1px 0 rgba(255,255,255, 0.1), inset 0 -1px 0 rgba(0,0,0, 0.1)',
              }}
              onClick={() => window.location.href = '/#contact'}
            >
              Demander un Devis Gratuit
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={IMAGES.BEDROOM_DESIGN_3}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/80" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-muted-foreground mb-4">
              YKE Multi-Service - Votre partenaire de prestige
            </p>
            <p className="text-sm text-muted-foreground">
              Sipim, Abidjan | +225 07 58 56 77 94 | YKE_Multi_Service@proton.me
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}